/**
 * ConsonantPracticeIntro - Introduction screen before consonant practice
 *
 * Displays instructions for parents/guardians about how to set up
 * for the consonant practice session with physical cards. Includes
 * an "Always record sessions" toggle that persists to profiles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Music, Users, ArrowRight, Mic, MicOff } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import { isMediaDevicesSupported, requestMicrophone, stopMediaStream } from '@/lib/media';
import { useTranslation } from '@/lib/i18n';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

interface ConsonantPracticeIntroProps {
  childName: string;
  onStartSession: (recordingEnabled: boolean) => void;
}

// ============================================================================
// Component
// ============================================================================

export const ConsonantPracticeIntro: React.FC<ConsonantPracticeIntroProps> = ({
  childName,
  onStartSession,
}) => {
  const { profile, refetchProfile } = useAuth();
  const { t } = useTranslation();
  const [alwaysRecord, setAlwaysRecord] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (profile?.always_record_sessions) setAlwaysRecord(true);
  }, [profile?.always_record_sessions]);

  const handleToggleRecording = useCallback(async () => {
    const newValue = !alwaysRecord;
    setToggling(true);

    if (newValue) {
      if (!isMediaDevicesSupported()) {
        toast.error(t('Your browser does not support microphone access.'));
        setToggling(false);
        return;
      }

      try {
        const stream = await requestMicrophone({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });
        stopMediaStream(stream);
      } catch {
        toast.error(t('Microphone permission denied. Please allow access in your browser settings.'));
        setToggling(false);
        return;
      }
    }

    if (profile?.id) {
      const { error } = await (supabase as any)
        .from('profiles')
        .update({ always_record_sessions: newValue })
        .eq('user_id', profile.user_id);

      if (error) {
        console.error('[ConsonantPracticeIntro] Failed to save recording preference:', error);
        toast.error(t('Failed to save recording preference.'));
        setToggling(false);
        return;
      }
    }

    setAlwaysRecord(newValue);
    await refetchProfile();
    toast.success(newValue ? t('Sessions will be recorded.') : t('Recording disabled.'));
    setToggling(false);
  }, [alwaysRecord, profile, refetchProfile]);

  return (
    <div className="flex flex-col items-center justify-center px-6 py-8 min-h-[60vh]">
      <div className="max-w-md w-full text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-6">
            <Music className="w-10 h-10 text-[#34C759]" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-semibold text-[#1C1C1E] mb-4">
            {t("Let's practice consonants!")}
          </h1>

          {/* Instructions Card */}
          <div
            className="rounded-2xl p-6 mb-6 text-left"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-[#FFD60A]" />
              <span className="text-[#FFD60A] font-medium text-sm">
                {t('Parent or guardian participation needed')}
              </span>
            </div>

            <p className="text-[#1C1C1E] leading-relaxed">
              {t('Please sit together at a table or on the floor with your cards displayed letters-up in front of you.')}
            </p>

            <ul className="mt-4 space-y-2 text-[#636366] text-sm">
              <li className="flex items-start gap-2">
                <span className="text-[#34C759]">1.</span>
                <span>{t('Have your Turn letter cards ready')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#34C759]">2.</span>
                <span>{t('The camera will be at the top of the screen')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#34C759]">3.</span>
                <span>{t('Ava will tell you which card to scan next')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#34C759]">4.</span>
                <span>{t('Practice the sound together with')} {childName}</span>
              </li>
            </ul>
          </div>

          {/* Recording Toggle */}
          <button
            onClick={handleToggleRecording}
            disabled={toggling}
            className={`w-full flex items-center gap-3 px-5 py-4 rounded-xl mb-6 transition-all border text-left min-h-[56px] ${
              alwaysRecord
                ? 'bg-[#111111]/10 border-[#1A1A1A]/30'
                : 'bg-black/[0.03] border-black/[0.08] hover:bg-black/[0.04]'
            } disabled:opacity-50`}
          >
            {alwaysRecord ? (
              <Mic className="w-5 h-5 text-[#1A1A1A] flex-shrink-0" />
            ) : (
              <MicOff className="w-5 h-5 text-[#8E8E93] flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${alwaysRecord ? 'text-[#1A1A1A]' : 'text-[#636366]'}`}>
                {alwaysRecord ? t('Recording enabled') : t('Record sessions')}
              </p>
              <p className="text-xs text-[#8E8E93] mt-0.5">
                {alwaysRecord
                  ? t('Audio will be captured for progress tracking')
                  : t('Enable to record audio for teacher review')}
              </p>
            </div>
            <div className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${
              alwaysRecord ? 'bg-[#111111]' : 'bg-black/[0.08]'
            }`}>
              <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform ${
                alwaysRecord ? 'translate-x-[18px]' : 'translate-x-0.5'
              }`} />
            </div>
          </button>

          {/* Start Button */}
          <button
            onClick={() => onStartSession(alwaysRecord)}
            className="w-full py-4 bg-[#34C759] hover:brightness-110 text-[#1C1C1E] rounded-xl font-medium flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#34C759]/20"
          >
            {t('Start Session')}
            <ArrowRight className="w-5 h-5" />
          </button>
      </div>
    </div>
  );
};

export default ConsonantPracticeIntro;
